// Filter preferences, in localStorage, and their translation into query params.
//
// Kept in a SEPARATE key from location prefs (`amc.location.v1`) on purpose:
// the two change on different schedules, and a malformed filter blob must not
// also cost you your saved theaters. `loadFilters` repairs field by field for
// the same reason -- one bad value falls back to its own default rather than
// resetting the lot.

const KEY = "amc.filters.v1";

export interface Filters {
  hideSeen: boolean;
  alistOnly: boolean;
  watchlistOnly: boolean;
  hideNotInterested: boolean;
  premiumOnly: boolean;
  /**
   * Only films the weekly model flags as likely leaving soon.
   *
   * Unlike every other filter here this selects whole FILMS rather than
   * screenings, because the closing badge is decided per grouped movie on the
   * server. Inert on Double features, whose response carries no badges at all
   * -- the control is hidden there rather than silently doing nothing.
   */
  closingOnly: boolean;
  /**
   * Only films Sarah said yes to and has not seen yet -- the films whose card
   * says "Sarah wants to see this". Whole films, applied after grouping on the
   * server like `closingOnly`, and inert on Double features for the same
   * reason.
   */
  sarahOnly: boolean;
  /** Empty means "any premium format", matching the server. */
  premiumFormats: string[];
  /** Both null, or both set. A half-set range filters nothing. */
  startTimeMin: number | null;
  startTimeMax: number | null;
}

/**
 * The three that start ON are the Streamlit page's defaults, carried over so
 * the site opens showing the same films it always has.
 *
 * The API itself defaults every filter OFF -- a bare `GET /api/showtimes` is
 * literal about what is playing. These defaults belong to the page, not the
 * endpoint, which is why they live here and are sent explicitly.
 */
export const DEFAULT_FILTERS: Filters = {
  hideSeen: true,
  alistOnly: true,
  watchlistOnly: false,
  hideNotInterested: true,
  premiumOnly: false,
  closingOnly: false,
  sarahOnly: false,
  premiumFormats: [],
  startTimeMin: null,
  startTimeMax: null,
};

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

/** An hour of the day, or null. Anything outside 0-23 would 422 at the API. */
const hour = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 23
    ? value
    : null;

export function loadFilters(): Filters {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_FILTERS;
    const saved = parsed as Partial<Filters>;

    const startTimeMin = hour(saved.startTimeMin);
    const startTimeMax = hour(saved.startTimeMax);
    // A range is only meaningful with both ends, and an inverted one would hide
    // everything -- so either survives whole or neither does.
    const rangeIsUsable =
      startTimeMin !== null && startTimeMax !== null && startTimeMin <= startTimeMax;

    return {
      hideSeen: bool(saved.hideSeen, DEFAULT_FILTERS.hideSeen),
      alistOnly: bool(saved.alistOnly, DEFAULT_FILTERS.alistOnly),
      watchlistOnly: bool(saved.watchlistOnly, DEFAULT_FILTERS.watchlistOnly),
      hideNotInterested: bool(saved.hideNotInterested, DEFAULT_FILTERS.hideNotInterested),
      premiumOnly: bool(saved.premiumOnly, DEFAULT_FILTERS.premiumOnly),
      closingOnly: bool(saved.closingOnly, DEFAULT_FILTERS.closingOnly),
      sarahOnly: bool(saved.sarahOnly, DEFAULT_FILTERS.sarahOnly),
      // Strings only: these are appended to a request URL, and the server drops
      // any label it no longer knows.
      premiumFormats: Array.isArray(saved.premiumFormats)
        ? saved.premiumFormats.filter((f): f is string => typeof f === "string")
        : [],
      startTimeMin: rangeIsUsable ? startTimeMin : null,
      startTimeMax: rangeIsUsable ? startTimeMax : null,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

export function saveFilters(filters: Filters): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(filters));
  } catch {
    // Storage blocked or full. The filters still work for this session.
  }
}

/**
 * Filters as `/api/showtimes` query params.
 *
 * Only non-default values are appended, so the URL stays readable and a filter
 * that is off is absent rather than `=false`. The API's defaults are all off,
 * which is what makes omission safe: `hideSeen: false` and "no hide_seen param"
 * mean the same thing to the server.
 */
export function filterParams(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.hideSeen) params.set("hide_seen", "true");
  if (filters.alistOnly) params.set("alist_only", "true");
  if (filters.watchlistOnly) params.set("watchlist_only", "true");
  if (filters.hideNotInterested) params.set("hide_not_interested", "true");
  if (filters.closingOnly) params.set("closing_only", "true");
  if (filters.sarahOnly) params.set("sarah_only", "true");
  if (filters.premiumOnly) {
    params.set("premium_only", "true");
    for (const format of filters.premiumFormats) params.append("premium_formats", format);
  }
  // Both ends or neither: sending one alone leaves the server's `filter_by_time`
  // off, so it would look like a filter that silently does nothing.
  if (filters.startTimeMin !== null && filters.startTimeMax !== null) {
    params.set("start_time_min", String(filters.startTimeMin));
    params.set("start_time_max", String(filters.startTimeMax));
  }
  return params;
}

/**
 * Whether anything differs from the page defaults, for a "reset" affordance.
 *
 * Derived from DEFAULT_FILTERS' own keys rather than listing the fields again.
 * A hand-written comparison goes stale the moment a filter is added, and the
 * symptom is silent: the Reset button just stops appearing once the new field
 * is the only one changed, which is precisely when it is needed. Nothing would
 * catch it either, since a hand-enumerated test rots in lockstep with the
 * hand-enumerated implementation.
 */
export function isDefault(filters: Filters): boolean {
  return (Object.keys(DEFAULT_FILTERS) as (keyof Filters)[]).every((key) => {
    const current = filters[key];
    const fallback = DEFAULT_FILTERS[key];
    // Every array default here is empty, so a length check settles it. A
    // deep element-by-element comparison would never actually compare an
    // element -- it only looked more thorough.
    return Array.isArray(current) && Array.isArray(fallback)
      ? current.length === fallback.length
      : current === fallback;
  });
}
